import { useRef, useLayoutEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useSiteConfig, resolveImageUrl } from "../config/SiteConfigContext";
import { useCmsText } from "../hooks/useCmsText";
import { useIsMobile } from "../hooks/useIsMobile";
import { RichText } from "../components/RichText";
import { Parallax } from "react-scroll-parallax";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { pathFor } from "../app/paths";

gsap.registerPlugin(ScrollTrigger);

const FALLBACK_HERO = "https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=1400&h=800&fit=crop";

export function Fiscalita() {
  const { locale } = useParams();
  const cfg = useSiteConfig();
  const ct = useCmsText("fiscalita");
  const isMobile = useIsMobile();
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(".gs-fi-hero-content > *", { opacity: 0, y: 40 }, { opacity: 1, y: 0, duration: 1.3, ease: "expo.out", stagger: 0.15, delay: 0.2 });

      gsap.utils.toArray<HTMLElement>(".gs-fi-reveal").forEach((el) => {
        gsap.fromTo(el, { opacity: 0, y: 50 }, { opacity: 1, y: 0, duration: 1.2, ease: "expo.out", scrollTrigger: { trigger: el, start: "top 82%", once: true } });
      });

      gsap.utils.toArray<HTMLElement>(".gs-fi-from-left").forEach((el) => {
        gsap.fromTo(el, { x: -60, opacity: 0 }, { x: 0, opacity: 1, duration: 1.3, ease: "expo.out", scrollTrigger: { trigger: el, start: "top 82%", once: true } });
      });

      gsap.utils.toArray<HTMLElement>(".gs-fi-from-right").forEach((el) => {
        gsap.fromTo(el, { x: 60, opacity: 0 }, { x: 0, opacity: 1, duration: 1.3, ease: "expo.out", scrollTrigger: { trigger: el, start: "top 82%", once: true } });
      });

      gsap.utils.toArray<HTMLElement>(".gs-fi-stagger").forEach((el) => {
        gsap.fromTo(el.children, { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.9, stagger: 0.1, ease: "expo.out", scrollTrigger: { trigger: el, start: "top 82%", once: true } });
      });

      gsap.utils.toArray<HTMLElement>(".gs-fi-line").forEach((el) => {
        gsap.fromTo(el, { width: 0 }, { width: el.dataset.w || "48", duration: 1.2, ease: "expo.out", scrollTrigger: { trigger: el, start: "top 90%", once: true } });
      });

      gsap.utils.toArray<HTMLElement>(".gs-fi-number").forEach((el) => {
        const target = parseInt(el.dataset.to || "0");
        gsap.fromTo(el, { textContent: "0" }, {
          textContent: target, duration: 2.2, ease: "power2.out", snap: { textContent: 1 },
          scrollTrigger: { trigger: el, start: "top 85%", once: true },
        });
      });
    }, containerRef);
    return () => ctx.revert();
  }, []);

  return (
    <div ref={containerRef} style={{ overflow: "hidden" }}>

      {/* ═══ HERO — Cinematic like Home ═══ */}
      <section style={{ position: "relative", height: isMobile ? "65vh" : "75vh", maxHeight: 640, minHeight: isMobile ? 380 : 460, overflow: "hidden" }}>
        <Parallax speed={isMobile ? -8 : -20} style={{ position: "absolute", inset: "-25% 0", height: "150%" }}>
          <div style={{ width: "100%", height: "100%", backgroundImage: `url(${ct.image("heroImage") ? resolveImageUrl(ct.image("heroImage")!) : FALLBACK_HERO})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        </Parallax>
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.3) 40%, rgba(0,0,0,0.75) 100%)" }} />

        <div className="gs-fi-hero-content" style={{ position: "absolute", bottom: 0, left: 0, right: 0, maxWidth: 1280, margin: "0 auto", padding: isMobile ? "0 24px 48px" : "0 48px 72px", zIndex: 2 }}>
          <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase", color: cfg.color_accent, margin: "0 0 16px" }}>
            {ct("overline")}
          </p>
          <h1 style={{ fontFamily: cfg.font_heading, fontSize: isMobile ? 32 : 52, fontWeight: 400, color: "#fff", margin: "0 0 16px", lineHeight: 1.05, letterSpacing: -0.5, maxWidth: isMobile ? 260 : 480 }}>
            {ct("heroTitle")}
          </h1>
          <RichText html={ct("heroSubtitle")} style={{ fontSize: isMobile ? 13 : 15, color: "#fff", maxWidth: 400, lineHeight: 1.8 }} />
        </div>
      </section>

      {/* ═══ INTRO — Full-width editorial ═══ */}
      <section style={{ background: cfg.color_body_bg, padding: isMobile ? "80px 24px" : "140px 48px" }}>
        <div className="gs-fi-reveal" style={{ maxWidth: 720, margin: "0 auto" }}>
          <div className="gs-fi-line" data-w="48" style={{ height: 1, background: cfg.color_muted, marginBottom: 24 }} />
          <h2 style={{ fontFamily: cfg.font_heading, fontSize: isMobile ? 26 : 38, fontWeight: 400, color: cfg.color_dark, margin: "0 0 24px", lineHeight: 1.2, letterSpacing: -0.5 }}>
            {ct("introTitle")}
          </h2>
          <RichText html={ct("introText")} style={{ fontSize: isMobile ? 15 : 17, color: "rgba(28,75,59,0.86)", lineHeight: 1.9 }} />
        </div>
      </section>

      {/* ═══ 50€ RULE — Hero number + editorial ═══ */}
      <section style={{ background: cfg.color_dark, padding: isMobile ? "72px 24px" : "120px 48px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: isMobile ? "block" : "grid", gridTemplateColumns: "1fr 1.2fr", gap: 80, alignItems: "center" }}>
          <div className="gs-fi-from-left" style={{ textAlign: isMobile ? "center" : "left", marginBottom: isMobile ? 48 : 0 }}>
            <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase", color: cfg.color_accent, margin: "0 0 16px" }}>
              {ct("keyRule")}
            </p>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: isMobile ? "center" : "flex-start", gap: 4 }}>
              <span className="gs-fi-number" data-to="50" style={{ fontFamily: cfg.font_heading, fontSize: isMobile ? 80 : 120, fontWeight: 400, color: "#fff", lineHeight: 1, letterSpacing: -3 }}>
                0
              </span>
              <span style={{ fontFamily: cfg.font_heading, fontSize: isMobile ? 32 : 48, fontWeight: 400, color: "#fff" }}>
                &euro;
              </span>
            </div>
            <div style={{ width: 48, height: 1, background: "rgba(255,255,255,0.1)", margin: isMobile ? "20px auto 16px" : "20px 0 16px" }} />
            <RichText html={ct("ruleExplanation")} style={{ fontSize: 14, color: "#fff", lineHeight: 1.8, maxWidth: 360 }} />
          </div>

          <div className="gs-fi-from-right">
            <h3 style={{ fontFamily: cfg.font_heading, fontSize: isMobile ? 22 : 28, fontWeight: 400, color: "#fff", margin: "0 0 18px", letterSpacing: -0.3 }}>
              {ct("lawTitle")}
            </h3>
            <RichText html={ct("lawText")} style={{ fontSize: 15, color: "#fff", lineHeight: 1.85, marginBottom: 24 }} />
            <div style={{ padding: "20px 24px", borderLeft: `2px solid ${cfg.color_accent}`, background: "rgba(255,255,255,0.03)" }}>
              <RichText html={ct("lawQuote")} style={{ fontSize: 14, color: "#fff", lineHeight: 1.8, fontStyle: "italic" }} />
            </div>
          </div>
        </div>
      </section>

      {/* ═══ BENEFITS — Three pillars ═══ */}
      <section style={{ background: cfg.color_body_bg, padding: isMobile ? "80px 24px" : "140px 48px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div className="gs-fi-reveal" style={{ marginBottom: isMobile ? 48 : 72, maxWidth: 500 }}>
            <div className="gs-fi-line" data-w="48" style={{ height: 1, background: cfg.color_muted, marginBottom: 24 }} />
            <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase", color: cfg.color_muted, margin: "0 0 12px" }}>
              {ct("benefitsOverline")}
            </p>
            <h2 style={{ fontFamily: cfg.font_heading, fontSize: isMobile ? 26 : 38, fontWeight: 400, color: cfg.color_dark, margin: 0, letterSpacing: -0.5, lineHeight: 1.15 }}>
              {ct("benefitsTitle")}
            </h2>
          </div>

          <div className="gs-fi-stagger" style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: isMobile ? 40 : 64 }}>
            {[1, 2, 3].map((n) => (
              <div key={n}>
                <span style={{ fontSize: 56, fontWeight: 400, fontFamily: cfg.font_heading, color: "rgba(0,0,0,0.04)", lineHeight: 1, display: "block", marginBottom: 16 }}>
                  {String(n).padStart(2, "0")}
                </span>
                <h3 style={{ fontFamily: cfg.font_heading, fontSize: isMobile ? 20 : 22, fontWeight: 400, color: cfg.color_dark, margin: "0 0 12px", letterSpacing: -0.2 }}>
                  {ct(`benefit${n}Title`)}
                </h3>
                <RichText html={ct(`benefit${n}Text`)} style={{ fontSize: 14, color: "rgba(28,75,59,0.86)", lineHeight: 1.85 }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ HOW GRANBORGO HELPS ═══ */}
      <section style={{ background: cfg.color_light_bg, padding: isMobile ? "80px 24px" : "120px 48px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", display: isMobile ? "block" : "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "start" }}>
          <div className="gs-fi-from-left">
            <div className="gs-fi-line" data-w="48" style={{ height: 1, background: cfg.color_muted, marginBottom: 24 }} />
            <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase", color: cfg.color_accent, margin: "0 0 12px" }}>
              {ct("granborgoCta")}
            </p>
            <h2 style={{ fontFamily: cfg.font_heading, fontSize: isMobile ? 26 : 34, fontWeight: 400, color: cfg.color_dark, margin: "0 0 18px", letterSpacing: -0.3, lineHeight: 1.15 }}>
              {ct("granborgoTitle")}
            </h2>
            <RichText html={ct("granborgoText")} style={{ fontSize: 15, color: "rgba(28,75,59,0.86)", lineHeight: 1.85 }} />
          </div>

          <div className="gs-fi-from-right" style={{ marginTop: isMobile ? 40 : 0 }}>
            <div className="gs-fi-stagger">
              {[1, 2, 3, 4].map((n) => (
                <div key={n} style={{ display: "flex", alignItems: "center", gap: 16, padding: "20px 0", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                  <div style={{ width: 32, height: 32, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={cfg.color_accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <span style={{ fontSize: 15, color: cfg.color_dark, lineHeight: 1.5 }}>
                    {ct(`granborgoPoint${n}`)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ CTA — Premium dark close ═══ */}
      <section style={{ background: cfg.color_dark, padding: isMobile ? "80px 24px" : "120px 48px" }}>
        <div className="gs-fi-reveal" style={{ maxWidth: 1200, margin: "0 auto", display: isMobile ? "block" : "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 48 }}>
          <div style={{ maxWidth: 480, marginBottom: isMobile ? 32 : 0 }}>
            <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase", color: "#fff", margin: "0 0 14px" }}>
              {cfg.brand_name}
            </p>
            <h2 style={{ fontFamily: cfg.font_heading, fontSize: isMobile ? 26 : 36, fontWeight: 400, color: "#fff", margin: "0 0 14px", letterSpacing: -0.5, lineHeight: 1.15 }}>
              {ct("ctaTitle")}
            </h2>
            <RichText html={ct("ctaText")} style={{ fontSize: 14, color: "#fff", lineHeight: 1.8 }} />
          </div>

          <div style={{ display: "flex", gap: 12, flexShrink: 0 }}>
            <Link
              to={pathFor("collections", locale)}
              style={{ background: "#fff", color: cfg.color_dark, textDecoration: "none", padding: "14px 32px", fontSize: 11, fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase", transition: "opacity 0.2s", whiteSpace: "nowrap" }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.85"; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
            >
              {ct("ctaShop")}
            </Link>
            <Link
              to={pathFor("about", locale)}
              style={{ background: "transparent", color: "#fff", textDecoration: "none", padding: "14px 32px", fontSize: 11, fontWeight: 500, letterSpacing: 1.5, textTransform: "uppercase", border: "1px solid rgba(255,255,255,0.12)", transition: "border-color 0.3s", whiteSpace: "nowrap" }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.4)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)"; }}
            >
              {ct("ctaAbout")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
